import { environment } from '../environments/environment';
import { AuthData } from '../store/authStore';
import { ChatMessage } from '../store/chatStore';
import { refreshAccessToken } from './authService';

// Runs an authenticated request and, on a 401, refreshes the access token once
// and retries with the new token. If the refresh itself fails it throws (and
// authService has already flagged the auth failure).
async function withAuthRetry(
  token: string,
  requestFn: (token: string) => Promise<Response>
): Promise<Response> {
  const response = await requestFn(token);
  if (response.status !== 401) return response;

  const newToken = await refreshAccessToken();
  return requestFn(newToken);
}

interface SendMessageResponse {
  message: string;
  intent: string;
  operation: string;
  handler_name: string;
  session_id: string;
  success: boolean;
  requires_input: boolean;
  metadata: Record<string, unknown>;
}

export async function sendMessage(
  aiToken: string,
  sessionId: string,
  message: string,
  auth: AuthData
): Promise<ChatMessage> {
  const response = await withAuthRetry(aiToken, (token) =>
    fetch(`${environment.API_BASE_URL}/api/v1/app/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        session_id: sessionId,
        message,
        // channel: auth.platform,
        // region: auth.region,
        // user_type: auth.user_type,
      }),
    })
  );

  if (!response.ok) {
    throw new Error(`Send message failed with status ${response.status}`);
  }

  const data: SendMessageResponse = await response.json();
  return {
    id: (data.metadata['correlation_id'] as string) ?? `${Date.now()}`,
    role: 'ai',
    content: data.message,
    timestamp: new Date(),
  };
}

interface HistoryItem {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface HistoryResponse {
  items: HistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchChatHistory(
  aiToken: string,
  sessionId: string,
  auth: AuthData
): Promise<ChatMessage[]> {
  const params = new URLSearchParams({
    limit: '50',
    offset: '0',
  });

  const response = await withAuthRetry(aiToken, (token) =>
    fetch(
      `${environment.API_BASE_URL}/api/v1/app/conversations/${sessionId}/messages?${params}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'session-id': sessionId,
        },
      }
    )
  );

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`Failed to load chat history: ${response.status}`);
  }

  const data: HistoryResponse = await response.json();

  return data.items.map((item) => ({
    id: item.id,
    role: item.role === 'assistant' ? 'ai' : 'user',
    content: item.content,
    timestamp: new Date(item.created_at),
  }));
}
